import { useEffect, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { whatsappLink } from '@sbg/shared/utils';
import { AppNavLink } from '@/components/ui/AppLink';
import { buttonClasses } from '@/components/ui/button-classes';
import { Container } from '@/components/ui/Container';
import { VisuallyHidden } from '@/components/ui/VisuallyHidden';
import { track } from '@/lib/analytics';
import { whenNeeded } from '@/lib/hydration-gate';
import { useT } from '@/lib/i18n';
import { usePublicSettings } from '@/lib/site/public-settings';
import { DocumentCard } from './DocumentCard';
import { LEGAL_LINKS, SITEMAP_LINKS } from './nav-items';
import { socialLinks } from './social';
import { SocialLinks } from './SocialLinks';
import { Wordmark } from './Wordmark';
import { List } from '@/components/ui/List';

/** Evaluated once per page load; `suppressHydrationWarning` covers a pre-render that crossed New Year. */
const CURRENT_YEAR = new Date().getFullYear();

let footerMap: Promise<ComponentType> | undefined;

/**
 * The code behind the footer's map is its own chunk, fetched once the page is about to be used (`whenNeeded`). The
 * first render starts it — that is when the gate can still tell that server markup is being hydrated — and every
 * later render shares it. Not `React.lazy`: the slot shows a plain box until the chunk is there, so nothing suspends
 * while the page hydrates.
 */
function loadFooterMap(): Promise<ComponentType> {
  footerMap ??= whenNeeded({ markup: '[data-footer-map]' })
    .then(() => import('./FooterMap'))
    .then((loaded) => loaded.default);
  return footerMap;
}

/** The address as one flowing paragraph, a comma where a line ended without one (D-368; the data keeps its line breaks). */
function flowAddress(address: string): string {
  const lines = address
    .split(/\s*\n\s*/)
    .map((line) => line.trim())
    .filter(Boolean);
  return lines
    .map((line, index) => (index === lines.length - 1 || line.endsWith(',') ? line : `${line},`))
    .join(' ');
}

/** A Google Maps address search for the firm's address (works before any script, sends nothing until it is clicked). */
function mapSearchUrl(address: string | null | undefined): string | null {
  const text = address?.trim();
  return text
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(text.replace(/,?\s*\n\s*/g, ', '))}`
    : null;
}

/**
 * The compact map under the tagline, on every page — the contact page included (it has its own larger map too; the
 * owner wants the footer's there as well, D-324).
 * The pre-rendered page holds a framed box with a plain "Open in Google Maps" link (an address search, so it works
 * before any script and without it); `FooterMap` replaces the box when its chunk arrives, in a frame of the same
 * size. Nothing is sent to Google by either (D-316, D-318).
 */
function FooterMapSlot() {
  const t = useT();
  const { address } = usePublicSettings();
  const pending = typeof window === 'undefined' ? null : loadFooterMap();
  const [Frame, setFrame] = useState<ComponentType | null>(null);

  useEffect(() => {
    if (!pending) return undefined;
    let live = true;
    pending
      .then((component) => {
        if (live) setFrame(() => component);
      })
      .catch(() => undefined); // no chunk, no map: the link stays
    return () => {
      live = false;
    };
  }, [pending]);

  const search = mapSearchUrl(address);
  return (
    <div className="footer-map" data-footer-map="">
      {Frame ? (
        <Frame />
      ) : search ? (
        <div className="map-frame map-frame--footer">
          <div className="map-frame__facade">
            <a
              className={buttonClasses({ variant: 'ghost', size: 'sm' })}
              href={search}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('footer.openMap')}
              <VisuallyHidden> {t('common.opensInNewTab')}</VisuallyHidden>
            </a>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="footer-fact">
      <dt className="footer-fact__label">{label}</dt>
      <dd className="footer-fact__value">{children}</dd>
    </div>
  );
}

/** A value from the settings, or a clearly-marked `{{PLACEHOLDER}}` until the owner supplies it. */
function Placeholder({ token }: { token: string }) {
  return (
    <span className="footer-fact__value" data-placeholder="true">
      {`{{${token}}}`}
    </span>
  );
}

/**
 * Footer (contentinfo landmark), compact since D-368: one row of four blocks on a desktop — the brand (logo and one
 * line), the pages (two to a row), the contact details in tight label / value rows (the address as one flowing
 * paragraph, phone, e-mail, WhatsApp, opening hours only if somebody sets them) and a small map — then a slim bottom
 * bar with the rights line, the networks as logos (ending with the map pin) and the rules and cookie choices. Two by
 * two on a tablet, one under the other on a phone, where the catalogue card sits above the bottom bar. The map is
 * a panel until the visitor asks for it (nothing is sent to Google). `{{PLACEHOLDER}}` only if the owner clears a value.
 */
export function Footer() {
  const t = useT();
  const settings = usePublicSettings();

  const whatsapp = settings.whatsappNumber
    ? whatsappLink(settings.whatsappNumber, t('footer.whatsappGreeting'))
    : null;
  const phoneHref = settings.phone ? `tel:${settings.phone.replace(/[^\d+]/g, '')}` : null;
  const socials = socialLinks(settings.social);
  // one map pin among the follow icons: the address in words and the link to it on Google Maps (D-329)
  const pinHref = mapSearchUrl(settings.address);
  const pin = pinHref
    ? {
        href: pinHref,
        address: settings.address?.replace(/,?\s*\n\s*/g, ', ').trim() ?? '',
        label: t('footer.address'),
      }
    : null;

  // D-368: the footer was redesigned (compact, one row of four blocks, a slim bottom bar). The previous markup is kept
  // below as a comment.
  return (
    <footer className="site-footer">
      <Container>
        <div className="footer-main">
          {/* Brand: the logo and one line about the firm (the networks are in the bottom bar) */}
          <div className="footer-brand">
            <Wordmark caption />
            <p className="footer-brand__tagline">{t('footer.tagline')}</p>
          </div>

          {/* Pages: the same links as the header, two to a row */}
          <nav aria-label={t('nav.footer')} className="footer-nav">
            <h2 className="footer-heading t-eyebrow">{t('footer.explore')}</h2>
            <List className="footer-list footer-list--cols">
              {SITEMAP_LINKS.map(({ to, labelKey }) => (
                <li key={to}>
                  <AppNavLink to={to} className="footer-link footer-link--tight">
                    {t(labelKey)}
                  </AppNavLink>
                </li>
              ))}
            </List>
          </nav>

          {/* Contact: the address, phone, e-mail and WhatsApp in tight rows */}
          <section aria-labelledby="footer-contact-heading" className="footer-contact">
            <h2 id="footer-contact-heading" className="footer-heading t-eyebrow">
              {t('footer.getInTouch')}
            </h2>
            <dl className="footer-facts">
              <Fact label={t('footer.address')}>
                {settings.address ? (
                  <span className="footer-fact__address">{flowAddress(settings.address)}</span>
                ) : (
                  <Placeholder token="ADDRESS" />
                )}
              </Fact>
              <Fact label={t('footer.phone')}>
                {phoneHref && settings.phone ? (
                  <a href={phoneHref} className="underline-offset-4 hover:underline">
                    {settings.phone}
                  </a>
                ) : (
                  <Placeholder token="PHONE" />
                )}
              </Fact>
              <Fact label={t('footer.email')}>
                {settings.email ? (
                  <a
                    href={`mailto:${settings.email}`}
                    className="underline-offset-4 hover:underline"
                  >
                    {settings.email}
                  </a>
                ) : (
                  <Placeholder token="CONTACT_EMAIL" />
                )}
              </Fact>
              {settings.hours ? (
                <Fact label={t('footer.hours')}>
                  <span className="footer-fact__multiline">{settings.hours}</span>
                </Fact>
              ) : null}
              {whatsapp ? (
                <Fact label={t('footer.whatsapp')}>
                  <a
                    href={whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline-offset-4 hover:underline"
                    onClick={() => {
                      track('whatsapp_click', {}); // analytics: consent-gated, no property
                    }}
                  >
                    {t('footer.chatOnWhatsApp')}
                    <VisuallyHidden> {t('common.opensInNewTab')}</VisuallyHidden>
                  </a>
                </Fact>
              ) : null}
            </dl>
          </section>

          {/* The map, small (nothing is sent to Google until the visitor asks, D-316) */}
          <div className="footer-visit">
            <FooterMapSlot />
          </div>
        </div>

        {/* phones only: the top strip is hidden there (D-360) */}
        <div className="footer-document">
          <DocumentCard />
        </div>

        {/* The bottom bar: the rights line, the rules and the cookie choices on one line where there is room */}
        <div className="footer-legal footer-bar">
          <p className="footer-bar__rights t-small text-fg-muted" suppressHydrationWarning>
            {t('footer.rights', { year: CURRENT_YEAR })}
          </p>
          {socials.length > 0 || pin ? (
            <section aria-labelledby="footer-social-heading" className="footer-social">
              <h2 id="footer-social-heading" className="sr-only">
                {t('footer.follow')}
              </h2>
              <SocialLinks links={socials} location={pin} />
            </section>
          ) : null}
          <nav aria-label={t('footer.legal')}>
            <List className="footer-bar__links">
              {LEGAL_LINKS.map(({ to, labelKey }) => (
                <li key={to}>
                  <AppNavLink to={to} className="footer-link footer-link--tight">
                    {t(labelKey)}
                  </AppNavLink>
                </li>
              ))}
              <li>
                {/* a plain link: sitemap.xml is a file the build writes, not a page of the app */}
                <a
                  href="/sitemap.xml"
                  className="footer-link footer-link--tight footer-link--small"
                >
                  {t('footer.sitemap')}
                </a>
              </li>
            </List>
          </nav>
        </div>
      </Container>
    </footer>
  );
}

/* The footer before D-368:
     return (
       <footer className="site-footer">
         <Container>
           <div className="grid gap-12 sm:grid-cols-2 lg:grid-cols-[1.3fr_1fr_1.3fr_1fr]">
             {/* Brand * /}
             <div className="grid content-start gap-5">
               <Wordmark large caption />
               <p className="max-w-[22rem] t-small text-fg-muted">{t('footer.tagline')}</p>
               <FooterMapSlot />
             </div>

             {/* Sitemap * /}
             <nav aria-label={t('nav.footer')}>
               <h2 className="footer-heading t-eyebrow">{t('footer.explore')}</h2>
               <List className="footer-list">
                 {SITEMAP_LINKS.map(({ to, labelKey }) => (
                   <li key={to}>
                     <AppNavLink to={to} className="footer-link">
                       {t(labelKey)}
                     </AppNavLink>
                   </li>
                 ))}
               </List>
             </nav>

             {/* Contact facts * /}
             <section aria-labelledby="footer-contact-heading">
               <h2 id="footer-contact-heading" className="footer-heading t-eyebrow">
                 {t('footer.getInTouch')}
               </h2>
               <dl>
                 <Fact label={t('footer.address')}>
                   {settings.address ? (
                     <span className="footer-fact__multiline">{settings.address}</span>
                   ) : (
                     <Placeholder token="ADDRESS" />
                   )}
                 </Fact>
                 <Fact label={t('footer.phone')}>
                   {phoneHref && settings.phone ? (
                     <a href={phoneHref} className="underline-offset-4 hover:underline">
                       {settings.phone}
                     </a>
                   ) : (
                     <Placeholder token="PHONE" />
                   )}
                 </Fact>
                 <Fact label={t('footer.email')}>
                   {settings.email ? (
                     <a
                       href={`mailto:${settings.email}`}
                       className="underline-offset-4 hover:underline"
                     >
                       {settings.email}
                     </a>
                   ) : (
                     <Placeholder token="CONTACT_EMAIL" />
                   )}
                 </Fact>
                 {settings.hours ? (
                   <Fact label={t('footer.hours')}>
                     <span className="footer-fact__multiline">{settings.hours}</span>
                   </Fact>
                 ) : null}
                 {whatsapp ? (
                   <Fact label={t('footer.whatsapp')}>
                     <a
                       href={whatsapp}
                       target="_blank"
                       rel="noopener noreferrer"
                       className="underline-offset-4 hover:underline"
                       onClick={() => {
                         track('whatsapp_click', {}); // analytics: consent-gated, no property
                       }}
                     >
                       {t('footer.chatOnWhatsApp')}
                       <VisuallyHidden> {t('common.opensInNewTab')}</VisuallyHidden>
                     </a>
                   </Fact>
                 ) : null}
               </dl>
             </section>

             {/* Social + legal * /}
             <div className="grid content-start gap-10">
               {socials.length > 0 || pin ? (
                 <section aria-labelledby="footer-social-heading">
                   <h2 id="footer-social-heading" className="footer-heading t-eyebrow">
                     {t('footer.follow')}
                   </h2>
                   <SocialLinks links={socials} location={pin} />
                 </section>
               ) : null}

               <nav aria-label={t('footer.legal')}>
                 <h2 className="footer-heading t-eyebrow">{t('footer.legal')}</h2>
                 <List className="footer-list">
                   {LEGAL_LINKS.map(({ to, labelKey }) => (
                     <li key={to}>
                       <AppNavLink to={to} className="footer-link">
                         {t(labelKey)}
                       </AppNavLink>
                     </li>
                   ))}
                   <li>
                     <button type="button" className="footer-link" onClick={reopenConsent}>
                       {t('footer.cookiePreferences')}
                     </button>
                   </li>
                 </List>
               </nav>
             </div>
           </div>

           {/* phones only: the top strip is hidden there (D-360) * /}
           <div className="footer-document">
             <DocumentCard />
           </div>

           <div className="footer-legal">
             <p className="t-small text-fg-muted" suppressHydrationWarning>
               {t('footer.rights', { year: CURRENT_YEAR })}
             </p>
           </div>
         </Container>
       </footer>
     );
   }

  */
